import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { Checkbox } from '@shared/components/ui/checkbox';

export interface DeleteShopConfirmModalRef {
  open: (onConfirm: () => void) => void;
}

interface DeleteShopConfirmModalProps {
  onConfirm: () => void;
}

export const DeleteShopConfirmModal = forwardRef<DeleteShopConfirmModalRef, DeleteShopConfirmModalProps>(
  ({ onConfirm }, ref) => {
    const { t } = useTranslation('shopsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const [isChecked, setIsChecked] = useState(false);
    const [currentCallback, setCurrentCallback] = useState<(() => void) | null>(null);

    useImperativeHandle(ref, () => ({
      open: (confirmCallback: () => void) => {
        setCurrentCallback(() => confirmCallback);
        setIsChecked(false);
        modalRef.current?.openModal();
      },
    }));

    const handleConfirm = useCallback(() => {
      if (isChecked && currentCallback) {
        currentCallback();
        modalRef.current?.closeModal();
      }
    }, [isChecked, currentCallback]);

    const handleClose = useCallback(() => {
      setIsChecked(false);
      setCurrentCallback(null);
      modalRef.current?.closeModal();
    }, []);

    return (
      <ModalCenter
        ref={modalRef}
        title={t('confirmModals.deleteShop.title')}
        type={{
          isBlocking: false,
          onClose: handleClose,
        }}
      >
        <div className="space-y-4">
          <p className="text-sm text-icons mt-1">{t('confirmModals.deleteShop.message')}</p>
          <p className="text-sm text-icons">{t('confirmModals.deleteShop.messageWarning')}</p>

          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="confirm-checkbox"
              checked={isChecked}
              onCheckedChange={() => setIsChecked(!isChecked)}
              data-testid="confirm-checkbox"
            />
            <label
              htmlFor="confirm-checkbox"
              className="text-sm text-icons font-medium  peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t('confirmModals.deleteShop.confirmationText')}
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" size="lg" onClick={handleClose}>
              {t('confirmModals.deleteShop.cancelText')}
              <EscIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleConfirm}
              disabled={!isChecked}
              data-testid="confirm-button"
            >
              {t('confirmModals.deleteShop.confirmText')}
              <EnterIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

DeleteShopConfirmModal.displayName = 'DeleteShopConfirmModal';
